import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, LogOut, RefreshCw, Search } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { KIA_LOGO, MODELS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sk_admin_token";

const modelName = (id) => MODELS.find((m) => m.id === id)?.name || id;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceBookings, setServiceBookings] = useState(null);
  const [banner, setBanner] = useState({ text: "", enabled: false, presets: [] });
  const [presetDates, setPresetDates] = useState({ start: "", end: "" });
  const [taps, setTaps] = useState(null);

  const SERVICE_STATUSES = [
    { id: "new", label: "New" },
    { id: "contacted", label: "Contacted" },
    { id: "done", label: "Done" },
  ];

  const SERVICE_STATUS_STYLES = {
    new: "border-white/20 text-slate-300",
    contacted: "border-white/60 text-white",
    done: "border-white bg-white text-black",
  };

  const updateServiceStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/service-bookings/${id}/status`, { status }, auth);
      setServiceBookings((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const updateTapStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/callback-taps/${id}/status`, { status }, auth);
      setTaps((prev) => ({
        ...prev,
        recent: prev.recent.map((t) => (t.id === id ? { ...t, status } : t)),
      }));
      toast.success(status === "done" ? "Callback marked done" : "Callback reopened");
    } catch {
      toast.error("Could not update tap");
    }
  };

  const saveBanner = async () => {
    try {
      await axios.put(`${API}/admin/settings/banner`, { text: banner.text.trim(), enabled: banner.enabled, presets: banner.presets || [] }, auth);
      toast.success("Banner updated — live on the homepage");
    } catch {
      toast.error("Could not save banner");
    }
  };

  const addPreset = async () => {
    const text = banner.text.trim();
    if (!text) return toast.error("Type a message first");
    if (presetDates.start && presetDates.end && presetDates.end < presetDates.start) {
      return toast.error("End date must be after start date");
    }
    const entry = { text, start: presetDates.start || null, end: presetDates.end || null };
    const presets = [...(banner.presets || []).filter((p) => p.text !== text), entry];
    try {
      await axios.put(`${API}/admin/settings/banner`, { text, enabled: banner.enabled, presets }, auth);
      setBanner((b) => ({ ...b, presets }));
      setPresetDates({ start: "", end: "" });
      toast.success(entry.start && entry.end ? "Preset saved with schedule" : "Preset saved — tap it anytime to activate");
    } catch {
      toast.error("Could not save preset");
    }
  };

  const deletePreset = async (i) => {
    const presets = (banner.presets || []).filter((_, idx) => idx !== i);
    try {
      await axios.put(`${API}/admin/settings/banner`, { text: banner.text, enabled: banner.enabled, presets }, auth);
      setBanner((b) => ({ ...b, presets }));
      toast.success("Preset removed");
    } catch {
      toast.error("Could not remove preset");
    }
  };

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async (q = "") => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { ...auth, params: q ? { q } : {} });
      setEnquiries(data);
      const svc = await axios.get(`${API}/admin/service-bookings`, auth);
      setServiceBookings(svc.data);
      const bn = await axios.get(`${API}/settings/banner`);
      setBanner(bn.data || { text: "", enabled: false });
      const tp = await axios.get(`${API}/admin/callback-taps`, auth);
      setTaps(tp.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load enquiries");
      }
    }
  }, [auth]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, Sanjib Kia");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/enquiries/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sanjib-kia-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const STATUS_OPTIONS = [
    { id: "new", label: "New" },
    { id: "contacted", label: "Contacted" },
    { id: "booked", label: "Booked" },
    { id: "closed", label: "Closed" },
  ];

  const STATUS_STYLES = {
    new: "border-white/20 text-slate-300",
    contacted: "border-white/60 text-white",
    booked: "border-white bg-white text-black",
    closed: "border-white/30 text-slate-500",
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/status`, { status }, auth);
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const stats = useMemo(() => {
    if (!enquiries) return { total: 0, today: 0, week: 0 };
    const now = Date.now();
    const dayMs = 86400000;
    return {
      total: enquiries.length,
      today: enquiries.filter((x) => now - new Date(x.created_at).getTime() < dayMs).length,
      week: enquiries.filter((x) => now - new Date(x.created_at).getTime() < 7 * dayMs).length,
    };
  }, [enquiries]);

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#05141F] flex items-center justify-center px-5">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-sm bg-[#0D1822] border border-white/10 clip-card p-8">
          <div className="flex items-center gap-3 mb-8">
            <img src={KIA_LOGO} alt="Kia" className="h-5 w-auto invert mix-blend-screen" />
            <span className="font-display font-extrabold tracking-[0.18em] text-white text-sm">
              SANJIB <span className="text-[#FFFFFF]">KIA</span> · ADMIN
            </span>
          </div>
          <form onSubmit={login} className="space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2">Email</label>
              <input
                data-testid="admin-email-input"
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="kia-input w-full" placeholder="sales@sanjibkia.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2">Password</label>
              <input
                data-testid="admin-password-input"
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="kia-input w-full" placeholder="••••••••"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-[#FFFFFF]">{error}</p>}
            <button
              data-testid="admin-login-button"
              type="submit" disabled={loading}
              className="w-full bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 text-black text-xs font-semibold uppercase tracking-[0.18em] py-3.5 clip-notch transition-colors"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
          <a data-testid="admin-back-to-site-link" href="/" className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-white transition-colors">
            <ArrowLeft size={13} /> Back to website
          </a>
        </div>
      </div>
    );
  }

  const visibleMatch = (x) => statusFilter === "all" || (x.status || "new") === statusFilter;
  const countFor = (s) => (enquiries || []).filter((x) => (x.status || "new") === s).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#05141F] text-slate-200">
      <Toaster position="top-center" richColors />
      <header className="border-b border-white/10 bg-[#0A0F14]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={KIA_LOGO} alt="Kia" className="h-5 w-auto invert mix-blend-screen" />
            <span className="font-display font-extrabold tracking-[0.18em] text-white text-sm sm:text-base">
              SANJIB <span className="text-[#FFFFFF]">KIA</span>
            </span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 border border-white/10 px-2.5 py-1">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <a data-testid="admin-back-to-site-link" href="/" className="text-xs text-slate-400 hover:text-white transition-colors hidden sm:inline">View Site</a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.15em] px-4 py-2.5 transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["Total Enquiries", stats.total, "stat-total"],
            ["Today", stats.today, "stat-today"],
            ["This Week", stats.week, "stat-week"],
            ["Callback Taps", taps ? taps.total : "—", "stat-taps"],
          ].map(([label, value, id]) => (
            <div key={id} data-testid={`admin-${id}`} className="bg-[#0D1822] border border-white/10 clip-card p-5 sm:p-7">
              <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
              <p className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-white">{value}</p>
            </div>
          ))}
        </div>

        {taps?.recent?.length > 0 && (
          <div data-testid="callback-taps-list" className="mt-4 bg-[#0D1822] border border-white/10 clip-card p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">Recent Callback Requests</p>
            <div className="divide-y divide-white/5">
              {taps.recent.slice(0, 10).map((t) => (
                <div key={t.id} data-testid={`callback-tap-${t.id.slice(0, 8)}`} className="flex items-center justify-between gap-4 py-2.5">
                  <span className={`font-mono text-[11px] ${t.status === "done" ? "text-slate-600 line-through" : "text-slate-300"}`}>
                    {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                  <button
                    data-testid={`callback-tap-toggle-${t.id.slice(0, 8)}`}
                    onClick={() => updateTapStatus(t.id, t.status === "done" ? "pending" : "done")}
                    className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors duration-300 ${
                      t.status === "done"
                        ? "border-white bg-white text-black"
                        : "border-white/20 text-slate-400 hover:border-white/60 hover:text-white"
                    }`}
                  >
                    {t.status === "done" ? "Done" : "Mark Done"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-search-input"
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(search)}
              placeholder="Search name, phone or model..."
              className="kia-input w-full pl-10"
            />
          </div>
          <button
            data-testid="admin-search-button"
            onClick={() => load(search)}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white text-xs font-semibold uppercase tracking-[0.15em] px-5 py-3 transition-colors"
          >
            <RefreshCw size={13} /> Search
          </button>
          <button
            data-testid="admin-digest-button"
            onClick={async () => {
              try {
                await axios.post(`${API}/admin/digest`, {}, auth);
                toast.success("Lead summary emailed to sales@sanjibkia.com");
              } catch {
                toast.error("Could not send digest");
              }
            }}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.15em] px-5 py-3 transition-colors"
          >
            Send Summary
          </button>
          <button
            data-testid="admin-export-button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.15em] px-5 py-3 clip-notch transition-colors"
          >
            <Download size={13} /> Export CSV
          </button>
        </div>

        <div data-testid="admin-status-tabs" className="mt-6 flex flex-wrap gap-2">
          {[{ id: "all", label: "All" }, ...STATUS_OPTIONS].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-filter-${t.id}`}
              onClick={() => setStatusFilter(t.id)}
              className={`font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2 border transition-colors duration-300 ${
                statusFilter === t.id
                  ? "bg-[#FFFFFF] border-[#FFFFFF] text-black"
                  : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {t.label} ({t.id === "all" ? (enquiries || []).length : countFor(t.id)})
            </button>
          ))}
        </div>

        <div className="mt-4 bg-[#0D1822] border border-white/10 clip-card overflow-x-auto">
          <table data-testid="admin-enquiries-table" className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Reference", "Customer", "Contact", "Model", "Drive", "Schedule", "Status", "Submitted"].map((h) => (
                  <th key={h} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-5 py-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enquiries === null ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Loading...</td></tr>
              ) : enquiries.length === 0 ? (
                <tr><td colSpan={8} data-testid="admin-empty-state" className="px-5 py-10 text-center text-slate-500">No enquiries yet.</td></tr>
              ) : (
                enquiries.filter(visibleMatch).map((x) => (
                  <tr key={x.id} data-testid={`enquiry-row-${x.id.slice(0, 8)}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <td className="px-5 py-4 font-mono text-xs text-[#FFFFFF]">{x.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-5 py-4 text-white font-medium">{x.name}</td>
                    <td className="px-5 py-4">
                      <a href={`tel:${x.phone.replace(/\s/g, "")}`} className="hover:text-[#FFFFFF] transition-colors">{x.phone}</a>
                      {x.email && <p className="text-xs text-slate-500 mt-0.5">{x.email}</p>}
                    </td>
                    <td className="px-5 py-4">{modelName(x.model)}</td>
                    <td className="px-5 py-4">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${x.drive_type === "home" ? "border-[#FFFFFF]/50 text-[#FFFFFF]" : "border-white/15 text-slate-400"}`}>
                        {x.drive_type === "home" ? "Doorstep" : "Showroom"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-400">{x.preferred_date} · {x.time_slot}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`enquiry-status-select-${x.id.slice(0, 8)}`}
                        value={x.status || "new"}
                        onChange={(e) => updateStatus(x.id, e.target.value)}
                        className={`bg-transparent border font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 outline-none cursor-pointer transition-colors ${STATUS_STYLES[x.status || "new"]}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.id} value={s.id} className="bg-[#0D1822] text-slate-200">{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{new Date(x.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 className="font-display font-bold text-xl text-white tracking-tight">Service Bookings</h2>
            <button
              data-testid="admin-service-export-button"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/service-bookings/export`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) throw new Error();
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "sanjib-kia-service-bookings.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Service CSV downloaded");
                } catch {
                  toast.error("Export failed");
                }
              }}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.15em] px-5 py-3 transition-colors"
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
          <div className="bg-[#0D1822] border border-white/10 clip-card overflow-x-auto">
            <table data-testid="admin-service-table" className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Reference", "Customer", "Contact", "Model", "Service", "Schedule", "Pickup & Drop", "Status", "Submitted"].map((h) => (
                    <th key={h} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {serviceBookings === null ? (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Loading...</td></tr>
                ) : serviceBookings.length === 0 ? (
                  <tr><td colSpan={8} data-testid="admin-service-empty" className="px-5 py-10 text-center text-slate-500">No service bookings yet.</td></tr>
                ) : (
                  serviceBookings.map((x) => (
                    <tr key={x.id} data-testid={`service-row-${x.id.slice(0, 8)}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                      <td className="px-5 py-4 font-mono text-xs text-[#FFFFFF]">{x.id.slice(0, 8).toUpperCase()}</td>
                      <td className="px-5 py-4 text-white font-medium">{x.name}</td>
                      <td className="px-5 py-4">
                        <a href={`tel:${x.phone.replace(/\s/g, "")}`} className="hover:text-[#FFFFFF] transition-colors">{x.phone}</a>
                      </td>
                      <td className="px-5 py-4">{x.model}{x.reg_number ? <p className="text-xs text-slate-500 mt-0.5">{x.reg_number}</p> : null}</td>
                      <td className="px-5 py-4 text-slate-300">{x.service_type}</td>
                      <td className="px-5 py-4 text-slate-400">{x.preferred_date} · {x.time_slot}</td>
                      <td className="px-5 py-4">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${x.pickup_drop ? "border-[#FFFFFF]/50 text-[#FFFFFF]" : "border-white/15 text-slate-500"}`}>
                          {x.pickup_drop ? "Pickup" : "Drive-in"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`service-status-select-${x.id.slice(0, 8)}`}
                          value={x.status || "new"}
                          onChange={(e) => updateServiceStatus(x.id, e.target.value)}
                          className={`bg-transparent border font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 outline-none cursor-pointer transition-colors ${SERVICE_STATUS_STYLES[x.status || "new"]}`}
                        >
                          {SERVICE_STATUSES.map((s) => (
                            <option key={s.id} value={s.id} className="bg-[#0D1822] text-slate-200">{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4 text-slate-500 text-xs">{new Date(x.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-12 bg-[#0D1822] border border-white/10 clip-card p-6 sm:p-8 max-w-2xl">
          <h2 className="font-display font-bold text-xl text-white tracking-tight">Festive Offer Banner</h2>
          <p className="text-xs text-slate-500 mt-1.5">Shows as a red announcement strip at the top of the homepage.</p>
          <div className="mt-5 flex items-center gap-3">
            <button
              data-testid="banner-toggle"
              onClick={() => setBanner((b) => ({ ...b, enabled: !b.enabled }))}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                banner.enabled ? "bg-[#FFFFFF] border-[#FFFFFF] text-black" : "border-white/15 text-slate-400 hover:border-white/40"
              }`}
            >
              {banner.enabled ? "Banner On" : "Banner Off"}
            </button>
          </div>
          <textarea
            data-testid="banner-text-input"
            value={banner.text}
            onChange={(e) => setBanner((b) => ({ ...b, text: e.target.value }))}
            rows={3}
            maxLength={200}
            placeholder="e.g. Festive Exchange Mela — best price for your old car + exciting benefits on every new Kia!"
            className="kia-input w-full mt-4 resize-none"
          />
          {(banner.presets || []).length > 0 && (
            <div className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2.5">Saved presets — tap to activate now</p>
              <div className="flex flex-wrap gap-2">
                {banner.presets.map((p, i) => (
                  <span key={i} data-testid={`banner-preset-${i}`} className="inline-flex items-center gap-2 border border-white/15 text-slate-300 text-xs px-3 py-2 max-w-full">
                    <button
                      onClick={() => setBanner((b) => ({ ...b, text: p.text, enabled: true }))}
                      className="hover:text-[#FFFFFF] transition-colors text-left truncate max-w-[240px] sm:max-w-[360px]"
                    >
                      {p.text}
                    </button>
                    {p.start && p.end && (
                      <span className="font-mono text-[9px] text-[#FFFFFF] whitespace-nowrap">{p.start} → {p.end}</span>
                    )}
                    <button
                      data-testid={`banner-preset-delete-${i}`}
                      onClick={() => deletePreset(i)}
                      className="text-slate-500 hover:text-[#FFFFFF] transition-colors text-sm leading-none"
                      aria-label="Delete preset"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2">Auto-start (optional)</label>
              <input
                data-testid="banner-preset-start"
                type="date"
                value={presetDates.start}
                onChange={(e) => setPresetDates((d) => ({ ...d, start: e.target.value }))}
                className="kia-input w-full"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2">Auto-end (optional)</label>
              <input
                data-testid="banner-preset-end"
                type="date"
                value={presetDates.end}
                onChange={(e) => setPresetDates((d) => ({ ...d, end: e.target.value }))}
                className="kia-input w-full"
              />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-600">If a saved preset's dates include today, it automatically replaces the banner text on the homepage.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              data-testid="banner-save-button"
              onClick={saveBanner}
              className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.15em] px-6 py-3 clip-notch transition-colors"
            >
              Save Banner
            </button>
            <button
              data-testid="banner-preset-add-button"
              onClick={addPreset}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.15em] px-6 py-3 transition-colors"
            >
              Save as Preset
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
